<script setup>
    // Component
    import MainHeader from '@/components/HeaderComponent.vue';
    import Tab from '@/components/TabComponent.vue';
    import Tooltip from '@/components/TooltipComponent.vue';
    import Calendar from '@/components/CalendarComponent.vue';
    import CounselorBox from '@/components/ReserveCounselorBoxComponent.vue';
    import TimeSelect from '@/components/TimeSelectComponent.vue';
    import BaseSelect from '@/components/BaseSelectComponent.vue';
    import i18n from '@/common/i18n';

    import CounselorProfileComponent from '@/components/modal/CounselorProfileComponent.vue';
    import CounselingRoomGuidanceComponent from '@/components/modal/CounselingRoomGuidanceComponent.vue';

    // Store
    import { useCommonStore } from '@/stores/common/commonStore';
    import { useReserveStore } from '@/stores/reserve/reserveStore';
    import { useLanguageStore } from '@/stores/common/languageStore';

    // Vue
    import { computed, onMounted, reactive, ref, inject } from 'vue';
    import { useRouter } from 'vue-router';
    import { getCurrentInstance } from 'vue';

    // commonProject
    import utils from '@/commonProject/helpers/utils.js';

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    // ----------------------------
    // 定数
    // ----------------------------
    const router = useRouter();
    const commonStore = useCommonStore();
    const reserveStore = useReserveStore();
    const languageStore = useLanguageStore();

    const from = commonStore.from;

    const app = getCurrentInstance();
    const API_URL = app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S;

    const counselorProfileModalDispFlg = ref('');
    const counselorProfileComponent = ref();

    const counselingRoomGuidanceModalDispFlg = ref('');
    const counselingRoomGuidanceComponent = ref();

    // ----------------------------
    // リアクティブ変数
    // ----------------------------
    // 選択されているタブの座標
    const tabs = ref([]);

    // 本日の日付
    const currentDate = ref(new Date());

    // 「カレンダーから選択」タブのブロックのカレンダーで選択された日付。予約詳細画面からのルートでは、予約した日付が復元される。
    const selectedDate = ref();

    const message = ref('');
    const requestData = reactive(reserveStore.rsvdtlRequest);
    const selectedDate1 = ref('');
    const selectedTime1Start = ref('');
    const selectedTime1End = ref('');
    const selectedDate2 = ref('');
    const selectedTime2Start = ref('');
    const selectedTime2End = ref('');
    const selectedDate3 = ref('');
    const selectedTime3Start = ref('');
    const selectedTime3End = ref('');
    const isSelect = ref(false);
    // 年月
    const yearMonth = ref('');
    // カレンダー
    const calendarComponent = ref();
    // カウンセラー選択
    // const counselorBoxComponent = ref();

    const dateReset = ref(false);

    // ----------------------------
    // API関数
    // ----------------------------
    // 予約（予約方法）取得API
    async function getTabList() {
        const requestBody = {
            Lang: languageStore.getLanguageValue,
            ServicePk: requestData.ServicePk,
            CngMethodType: requestData.CngMethodType,
            CngLanguagePk: requestData.CngLanguagePk,
        };

        // セッショングループPKを前画面から引き継いでいる場合はリクエストボディに追加する。
        if (!utils.isNullEmpty(requestData.SessionGrpPk)) {
            requestBody['SessionGrpPk'] = requestData.SessionGrpPk;
        }

        const response = await reserveStore.getTabListAction(API_URL, requestBody);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    // 施設一覧取得
    async function getRoomList() {
        const requestBody = {
            Lang: languageStore.getLanguageValue,
            ServicePk: requestData.ServicePk,
            CngMethodType: requestData.CngMethodType,
            CngLanguagePk: requestData.CngLanguagePk,
        };

        // セッショングループPKを前画面から引き継いでいる場合はリクエストボディに追加する。
        if (!utils.isNullEmpty(requestData.SessionGrpPk)) {
            requestBody['SessionGrpPk'] = requestData.SessionGrpPk;
        }

        const response = await reserveStore.getCalendarRoomAction(API_URL, requestBody);
        if (response.success) {
            if (!utils.isNullEmpty(requestData.RoomPk)) {
                await getCalendarDay();
            }
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    // 地域選択取得
    async function getRegionList() {
        // 地域選択リクエスト
        const requestBody = {
            Lang: languageStore.getLanguageValue,
        };

        const response = await reserveStore.getArrangeRegionAction(API_URL, requestBody);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        } else {
            if (!utils.isNullEmpty(requestData.AreaPk)) {
                await getAreaList();
            }
        }
    }

    // 地区選択取得
    async function getAreaList() {
        // リクエスト
        const requestBody = {
            Lang: languageStore.getLanguageValue,
            RegionPk: requestData.RegionPk,
        };

        const response = await reserveStore.getArrangeAreaAction(API_URL, requestBody);
        if (response.success) {
            // 地区が現在の選択値が新しいリストに存在するか確認
            const optionExists = reserveStore.areaList.some((option) => option.pk === requestData.AreaPk);
            if (!optionExists) {
                // 存在しない場合選択値をリセット
                requestData.AreaPk = '';
            }
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    // 予約カレンダー日付一覧取得API
    async function getCalendarDay() {
        // リクエスト
        const requestBody = {
            Lang: languageStore.getLanguageValue,
            ServicePk: requestData.ServicePk,
            CngMethodType: requestData.CngMethodType,
            CngLanguagePk: requestData.CngLanguagePk,
            YearMonth: yearMonth.value,
        };

        // セッショングループPKを前画面から引き継いでいる場合はリクエストボディに追加する。
        if (!utils.isNullEmpty(requestData.SessionGrpPk)) {
            requestBody['SessionGrpPk'] = requestData.SessionGrpPk;
        }

        // 施設PKを前画面から引き継いでいる場合はリクエストボディに追加する。
        if (!utils.isNullEmpty(requestData.RoomPk)) {
            requestBody['RoomPk'] = requestData.RoomPk;
        }

        const response = await reserveStore.getCalendarDayAction(API_URL, requestBody);
        if (response.success) {
            if (utils.isNullEmpty(selectedDate.value)) {
                const date = setToday(selectedDate.value);
                if (!reserveStore.calendarTimeList.includes(date)) {
                    selectedDate.value = '';
                }
            }
            if (!utils.isNullEmpty(requestData.ReservationDt)) {
                if (setToday(currentDate.value) !== requestData.ReservationDt.split(' ')[0]) {
                    await getCalendarTime();
                } else {
                    requestData.ReservationDt = '';
                }
            }
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    // 予約カレンダー時刻一覧取得API
    async function getCalendarTime() {
        // リクエスト
        const requestBody = {
            Lang: languageStore.getLanguageValue,
            ServicePk: requestData.ServicePk,
            CngMethodType: requestData.CngMethodType,
            CngLanguagePk: requestData.CngLanguagePk,
            Date: setToday(selectedDate.value),
        };

        // セッショングループPKを前画面から引き継いでいる場合はリクエストボディに追加する。
        if (!utils.isNullEmpty(requestData.SessionGrpPk)) {
            requestBody['SessionGrpPk'] = requestData.SessionGrpPk;
        }

        // 施設PKを前画面から引き継いでいる場合はリクエストボディに追加する。
        if (!utils.isNullEmpty(requestData.RoomPk)) {
            requestBody['RoomPk'] = requestData.RoomPk;
        }
        // 予約Pk
        if (!utils.isNullEmpty(reserveStore.reservationPk)) {
            requestBody['ReservationPk'] = reserveStore.reservationPk;
        }

        const response = await reserveStore.getCalendarTimeAction(API_URL, requestBody);
        if (response.success) {
            if (reserveStore.counselorList2.length > 0) {
                isSelect.value = true;
            }
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    // 予約アレンジ日付一覧取得API
    async function getArrangeDay() {
        // 地域選択リクエスト
        const requestBody = {
            Lang: languageStore.getLanguageValue,
        };

        const response = await reserveStore.getArrangeDayAction(API_URL, requestBody);

        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    // サービス詳細API
    async function getServiceDetail() {
        // リクエスト
        const requestBody = {
            Lang: languageStore.getLanguageValue,
            ServicePk: requestData.ServicePk,
        };
        // サービス詳細取得API
        const response = await reserveStore.getServiceDetailAction(API_URL, requestBody);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return false;
        }
    }

    // ----------------------------
    // 関数
    // ----------------------------
    /**
     * バリデーションチェック
     */
    function isValidationError() {
        // エラーメッセージを初期化する。
        let message = '';

        // 「ご希望の場所・時間帯をご連絡」タブが選択されている場合は、本タブ内の予約設定情報の内容のバリデーションチェックを行う。
        if (requestData.CngReserveType === '2') {
            if (requestData.CngMethodType === '1') {
                // ご希望地域
                if (utils.isNullEmpty(requestData.RegionPk)) {
                    message += i18n.global.t('reservePlanningCounselor.message.message1') + '\n';
                }
                // ご希望地区
                if (utils.isNullEmpty(requestData.AreaPk)) {
                    message += i18n.global.t('reservePlanningCounselor.message.message2') + '\n';
                }
            }
            // ご希望日時1
            if (utils.isNullEmpty(selectedDate1.value) || utils.isNullEmpty(selectedTime1Start.value) || utils.isNullEmpty(selectedTime1End.value)) {
                message += i18n.global.t('reservePlanningCounselor.message.message3') + '\n';
            } else if (!reserveStore.arrangeTimeList.includes(convertDateHyphenToSlash(selectedDate1.value))) {
                selectedDate1.value = '';
                requestData.RequestDt1Start = '';
                requestData.RequestDt1End = '';
                message += i18n.global.t('reservePlanningCounselor.message.message10') + '\n';
            }
            // ご希望日時2
            if (utils.isNullEmpty(selectedDate2.value) || utils.isNullEmpty(selectedTime2Start.value) || utils.isNullEmpty(selectedTime2End.value)) {
                message += i18n.global.t('reservePlanningCounselor.message.message4') + '\n';
            } else if (!reserveStore.arrangeTimeList.includes(convertDateHyphenToSlash(selectedDate2.value))) {
                selectedDate2.value = '';
                requestData.RequestDt2Start = '';
                requestData.RequestDt2End = '';
                message += i18n.global.t('reservePlanningCounselor.message.message11') + '\n';
            }
            // ご希望日時3
            if (utils.isNullEmpty(selectedDate3.value) || utils.isNullEmpty(selectedTime3Start.value) || utils.isNullEmpty(selectedTime3End.value)) {
                message += i18n.global.t('reservePlanningCounselor.message.message5') + '\n';
            } else if (!reserveStore.arrangeTimeList.includes(convertDateHyphenToSlash(selectedDate3.value))) {
                selectedDate3.value = '';
                requestData.RequestDt3Start = '';
                requestData.RequestDt3End = '';
                message += i18n.global.t('reservePlanningCounselor.message.message12') + '\n';
            }
            // 終了時刻が開始時刻より早い
            if (requestData.RequestDt1Start > requestData.RequestDt1End || requestData.RequestDt2Start > requestData.RequestDt2End || requestData.RequestDt3Start > requestData.RequestDt3End) {
                message += i18n.global.t('reservePlanningCounselor.message.message13') + '\n';
            }
            // ご連絡事項
            if (!utils.isNullEmpty(requestData.RequestNote)) {
                if (utils.checkLengthOfLongString(requestData.RequestNote)) {
                    message += i18n.global.t('reservePlanningCounselor.message.message6') + '\n';
                }
            }
        } else {
            if (requestData.CngMethodType === '1') {
                // 予約場所
                if (utils.isNullEmpty(requestData.RoomPk)) {
                    message += i18n.global.t('reservePlanningCounselor.message.message7') + '\n';
                }
            }
            // 日付
            if (utils.isNullEmpty(selectedDate.value)) {
                message += i18n.global.t('reservePlanningCounselor.message.message8') + '\n';
            }
            // カウンセラー
            if (utils.isNullEmpty(requestData.CounselorPk)) {
                message += i18n.global.t('reservePlanningCounselor.message.message9') + '\n';
            }
        }

        // エラーメッセージが存在する場合は、以降の処理に進ませないので、バリデーションチェック判定を返却する。
        if (!utils.isNullEmpty(message)) {
            // エラーメッセージが存在する場合は、画面に表示する。
            messageDialog('error', message);
            return true;
        }

        return false;
    }

    // タブ変化時
    async function tabChange(id) {
        // タブを切り替えた座標を記録する。
        requestData.CngReserveType = id;
        isSelect.value = false;
        tabClear();
        // 「カレンダー」タブが選択されたので、カレンダータブ側の内容を初期化する。
        if (requestData.CngReserveType === '1') {
            // カレンダーに表示する日付の初期値は本日の日付
            selectedDate.value = new Date();
            updateYearMonth(selectedDate.value);

            // カレンダーが選択されている場合。
            if (requestData.CngMethodType === '1') {
                // 予約カレンダー施設一覧API
                await getRoomList();
            } else {
                // 予約カレンダー日付一覧取得API
                await getCalendarDay();
            }
        } else {
            // アレンジが選択されている場合。
            // 「ご希望の場所・時間帯をご連絡」タブが選択されたので、アレンジタブ側の内容を初期化する。
            // 画面表示上の情報を初期化する。
            selectedDate1.value = ''; // 希望日時1の日付
            selectedTime1Start.value = ''; // 希望日時1の開始時間
            selectedTime1End.value = ''; // 希望日時1の終了時間
            selectedDate2.value = ''; // 希望日時2の日付
            selectedTime2Start.value = ''; // 希望日時2の開始時間
            selectedTime2End.value = ''; // 希望日時2の終了時間
            selectedDate3.value = ''; // 希望日時3の日付
            selectedTime3Start.value = ''; // 希望日時3の開始時間
            selectedTime3End.value = ''; // 希望日時3の終了時間

            // カレンダーに表示する日付の初期値は本日の日付
            selectedDate.value = new Date();

            // 利用方法を「対面」で選択している場合。
            if (requestData.CngMethodType === '1') {
                // 予約アレンジ地域一覧取得API
                await getRegionList();
            }

            // 予約アレンジ日付一覧取得APIをコールする。
            await getArrangeDay();
        }
    }

    // 「ご希望地域」項目のプルダウンのチェンジイベント。
    // APIを実行し、「ご希望地区」のプルダウンを生成する。
    async function regionChange() {
        if (requestData.RegionPk === '') {
            reserveStore.areaList = [];
            requestData.AreaPk = '';
        } else {
            // 予約アレンジ地区一覧取得APIをコールする。
            await getAreaList();
        }
    }

    // 戻るボタン押下時
    function onBack() {
        // 前画面からの戻るフラグを有効にする。
        commonStore.reservePageBack = true;

        reserveStore.restoreFlag = false;
        // 取得済みの復元内容(reserveDetail)を破棄する
        reserveStore.clearReserveDetail();
        // requestData.value = reserveStore.rsvdtlRequest;

        // 遷移元予約画面のルートと、設定・選択状況に応じて、戻る画面を制御する。
        switch (from) {
            case 'consultation':
                // 相談内容を選んで予約のルートは対象外

                // 予約(規定同意)画面に戻る
                if (reserveStore.reserveStep.consultation.reserveplanningagree) {
                    router.push({ name: 'reserveplanningagree' });
                    return;
                }

                // 予約(相談内容からサービス選択)画面に戻る
                if (reserveStore.reserveStep.consultation.reserveplanningservice) {
                    router.push({ name: 'reserveplanningservice' });
                    return;
                }

                // 予約(相談内容から利用方法等選択)画面に戻る
                if (reserveStore.reserveStep.consultation.reserveplanningconsultation) {
                    router.push({ name: 'reserveplanningconsultation' });
                    return;
                }
                break;
            case 'program':
                // プログラムを選んで予約のルート

                // 予約(プログラムから利用方法など選択)画面
                if (reserveStore.reserveStep.program.reserveplanningselection) {
                    router.push({ name: 'reserveplanningselection' });
                    return;
                }

                // 予約(規定同意)画面に戻る
                if (reserveStore.reserveStep.program.reserveplanningagree) {
                    router.push({ name: 'reserveplanningagree' });
                    return;
                }

                // 予約(プログラムからサービス選択)画面
                if (reserveStore.reserveStep.program.reserveplanningprogram) {
                    router.push({ name: 'reserveplanningprogram' });
                    return;
                }
                break;
            case 'personal':
                // 割当てられたプログラムを予約のルート

                // 予約(プログラムから利用方法など選択)画面
                if (reserveStore.reserveStep.personal.reserveplanningselection) {
                    router.push({ name: 'reserveplanningselection' });
                    return;
                }

                // 予約(規定同意)画面に戻る
                if (reserveStore.reserveStep.personal.reserveplanningagree) {
                    router.push({ name: 'reserveplanningagree' });
                    return;
                }

                // 予約(プログラムからサービス選択)画面
                if (reserveStore.reserveStep.personal.reserveplanningpersonal) {
                    router.push({ name: 'reserveplanningpersonal' });
                    return;
                }
                break;
            case 'ReserveDetailView':
                // 予約詳細のルート

                // 予約詳細画面に戻る
                router.push({ name: 'reservedetail' });
                break;
            default:
                // 戻り先指定無し。
                break;
        }
    }
    // 進むボタン押下時
    function onAdvance() {
        // バリデーションチェック
        if (isValidationError()) {
            return;
        }
        if (from === 'ReserveDetailView') {
            if (requestData.CngReserveType === '2') {
                // 予約情報を初期化
                requestData.ReservationDt = '';
                requestData.RoomPk = '';
                requestData.CounselorPk = '';
            }
        }
        reserveStore.setRsvdtlRequestData(requestData);
        router.push({ name: 'reserveplanningconfirm' });
    }
    // 選択されたカレンダー日付
    async function calendarDateSelected(date) {
        // カウンセラーPKの初期化
        requestData.CounselorPk = '';
        selectedDate.value = date;
        // 予約カレンダー時刻一覧取得API
        await getCalendarTime();
    }
    // 選択された値生成用
    function formatDate(date) {
        if (utils.isNullEmpty(date)) {
            return null;
        }
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}/${month}/${day}`;
    }

    function radioChange(CounselorPk, Time) {
        requestData.CounselorPk = CounselorPk;
        requestData.ReservationDt = formatSelectedDate.value + ' ' + Time;
    }

    /**
     * date(日付)タイプをyyyy-mm-ddからyyyy/mm/ddに変換する
     * @param input yyyy-mm-dd
     */
    function convertDateHyphenToSlash(input) {
        if (input !== '') {
            const [year, month, day] = input.split('-');
            return `${year}/${month}/${day}`;
        } else {
            return '';
        }
    }

    /**
     * タブを更新する。
     */
    async function consultationSelection() {
        // 利用方法に応じて制御する。
        switch (requestData.CngMethodType) {
            case '1':
                // 利用方法を「対面」で選択している場合。
                {
                    if (requestData.CngReserveType === '1') {
                        // 予約カレンダー施設一覧APIをコールする。
                        // リクエストボディは前項で設定した構造を流用する。
                        await getRoomList();
                    } else if (requestData.CngReserveType === '2') {
                        // 予約アレンジ地域一覧取得APIをコールする。
                        await getRegionList();
                    }
                }
                break;
            case '2':
                // 利用方法を「電話」で選択している場合。
                {
                    if (reserveStore.serviceDetail.ServiceTelMet === '1') {
                        // 利用者から架電
                        message.value = i18n.global.t('reservePlanningCounselor.parts.pageInformation2');
                    } else {
                        // カウンセラーから架電
                        message.value = i18n.global.t('reservePlanningCounselor.parts.pageInformation3');
                    }
                }
                break;
            case '3':
                // 利用方法を「オンライン」で選択している場合。
                {
                    // 利用者かカウンセラーかで表示内容を変化
                    message.value = i18n.global.t('reservePlanningCounselor.parts.pageInformation1');
                }
                break;

            default:
                // 何もしない。
                break;
        }
        // 画面表示用のAPIを実行する。
        await changeCallApiByCngReserveTypeAndSelectedValue();
    }

    /**
     * 画面初期表示に各APIを実行する。
     * タブを切り替えた際にも実行するメソッド。
     */
    async function changeCallApiByCngReserveTypeAndSelectedValue() {
        // 「カレンダー」タブ、もしくは、「日時・時間帯」タブの選択に応じて制御する。
        switch (requestData.CngReserveType) {
            case '1':
                // 「カレンダー」タブが選択されている場合。
                // 予約カレンダー日付一覧取得APIをコールする。
                await getCalendarDay();
                break;
            case '2':
                // 予約アレンジ日付一覧取得APIをコールする。
                await getArrangeDay();
                break;
            default:
                // 何もしない。
                break;
        }
    }

    /**
     * カウンセラープロフィールモーダルを開くイベント処理
     */
    async function counselorProfileComponentOpen(CounselorPk) {
        counselorProfileComponent.value.openModal(API_URL, CounselorPk);
        counselorProfileModalDispFlg.value = 'block';
        document.body.classList.add('modal-open'); //スクロール停止
    }

    async function counselorProfileComponentClose() {
        counselorProfileModalDispFlg.value = '';
        document.body.classList.remove('modal-open'); //スクロール再開
    }

    /**
     * ルーム・施設モーダルを開くイベント処理
     */
    async function counselingRoomGuidanceOpen(RoomPk) {
        if (utils.isNullEmpty(RoomPk)) {
            messageDialog('error', i18n.global.t('reservePlanningCounselor.message.message7'));
            return;
        }
        counselingRoomGuidanceComponent.value.openModal(API_URL, RoomPk);
        counselingRoomGuidanceModalDispFlg.value = 'block';
        document.body.classList.add('modal-open'); //スクロール停止
    }

    async function counselingRoomGuidanceClose() {
        counselingRoomGuidanceModalDispFlg.value = '';
        document.body.classList.remove('modal-open'); //スクロール再開
    }

    function setToday(date) {
        const Y = date.getFullYear();
        const M = date.getMonth() + 1;
        const D = date.getDate();
        const new_M = M < 10 ? '0' + M : M;
        const new_D = D < 10 ? '0' + D : D;
        return Y + '/' + new_M + '/' + new_D;
    }

    /**
     * 予約場所のプルダウンの変更をトリガーにする。
     */
    async function changeRoom() {
        // 選択された日付カレンダー月の初めの日にする。
        const startYearMonth = `${yearMonth.value}/01`
        selectedDate.value = new Date(startYearMonth);
        updateYearMonth(selectedDate.value);
        // 初期化処理
        isSelect.value = false;
        reserveStore.calendarTimeList = [];
        reserveStore.counselorList = [];
        reserveStore.counselorList2 = [];
        requestData.ReservationDt = '';
        dateReset.value = true;
        // 施設選択肢が選ばれている場合カレンダーの予約カレンダー日付一覧取得APIをコールする。
        if (!utils.isNullEmpty(requestData.RoomPk)) {
            // 画面の表示を制御する。
            await changeCallApiByCngReserveTypeAndSelectedValue();
        }
        // コンポーネントの選択値の初期化
        calendarComponent.value.reset();
    }

    /**
     * iPhoneで操作時にカレンダーの動作が異なり、日付選択ができなくなるため日付チェックを進むボタンのvalidateに統合
     */
    // アレンジ用日付が有効かどうか確認する
    // function validDate(identifier) {
    //     // 無効な日付が選択された場合、リセット
    //     if (identifier === '1') {
    //         if (selectedDate1.value === '') {
    //             requestData.RequestDt1Start = '';
    //             requestData.RequestDt1End = '';
    //             return;
    //         }
    //         if (!reserveStore.arrangeTimeList.includes(convertDateHyphenToSlash(selectedDate1.value))) {
    //             selectedDate1.value = '';
    //             requestData.RequestDt1Start = '';
    //             requestData.RequestDt1End = '';
    //             alert(i18n.global.t('reservePlanningCounselor.message.message10'));
    //         }
    //     } else if (identifier === '2') {
    //         if (selectedDate2.value === '') {
    //             requestData.RequestDt2Start = '';
    //             requestData.RequestDt2End = '';
    //             return;
    //         }

    //         if (!reserveStore.arrangeTimeList.includes(convertDateHyphenToSlash(selectedDate2.value))) {
    //             selectedDate2.value = '';
    //             requestData.RequestDt2Start = '';
    //             requestData.RequestDt2End = '';
    //             alert(i18n.global.t('reservePlanningCounselor.message.message11'));
    //         }
    //     } else if (identifier === '3') {
    //         if (selectedDate3.value === '') {
    //             requestData.RequestDt3Start = '';
    //             requestData.RequestDt3End = '';
    //             return;
    //         }
    //         if (!reserveStore.arrangeTimeList.includes(convertDateHyphenToSlash(selectedDate3.value))) {
    //             selectedDate3.value = '';
    //             requestData.RequestDt3Start = '';
    //             requestData.RequestDt3End = '';
    //             alert(i18n.global.t('reservePlanningCounselor.message.message12'));
    //         }
    //     }
    // }

    // 時刻入力
    function dateSet(identifier) {
        switch (identifier) {
            case '1':
                if (selectedTime1Start.value !== '') {
                    requestData.RequestDt1Start = convertDateHyphenToSlash(selectedDate1.value) + ' ' + selectedTime1Start.value;
                }
                if (selectedTime1End.value !== '') {
                    requestData.RequestDt1End = convertDateHyphenToSlash(selectedDate1.value) + ' ' + selectedTime1End.value;
                }
                break;
            case '2':
                if (selectedTime2Start.value !== '') {
                    requestData.RequestDt2Start = convertDateHyphenToSlash(selectedDate2.value) + ' ' + selectedTime2Start.value;
                }
                if (selectedTime2End.value !== '') {
                    requestData.RequestDt2End = convertDateHyphenToSlash(selectedDate2.value) + ' ' + selectedTime2End.value;
                }
                break;
            case '3':
                if (selectedTime3Start.value !== '') {
                    requestData.RequestDt3Start = convertDateHyphenToSlash(selectedDate3.value) + ' ' + selectedTime3Start.value;
                }
                if (selectedTime3End.value !== '') {
                    requestData.RequestDt3End = convertDateHyphenToSlash(selectedDate3.value) + ' ' + selectedTime3End.value;
                }
                break;
            default:
                // 特になし
                break;
        }
    }

    // レスポンスデータをリクエストにマージ
    function mergeResponce(target, res) {
        for (const key in res) {
            if (key in target && !utils.isNullEmpty(res[key])) {
                target[key] = res[key];
            }
        }
        return target;
    }

    // タブ初期化処理
    function tabClear() {
        // カレンダータブ情報
        reserveStore.roomList = [];
        reserveStore.calendarTimeList = [];
        reserveStore.counselorList = [];
        requestData.RoomPk = '';
        requestData.CounselorPk = '';
        requestData.ReservationDt = '';
        // アレンジタブ情報
        reserveStore.regionList = [];
        reserveStore.areaList = [];
        requestData.RegionPk = '';
        requestData.AreaPk = '';
        requestData.RequestDt1Start = '';
        requestData.RequestDt1End = '';
        requestData.RequestDt2Start = '';
        requestData.RequestDt2End = '';
        requestData.RequestDt3Start = '';
        requestData.RequestDt3End = '';
        requestData.RequestNote = '';
    }

    // カレンダーの年月
    async function calendarYearMonth(date) {
        yearMonth.value = date;
        isSelect.value = false;
        requestData.ReservationDt = '';
        // 予約カレンダー日付一覧（施設優先）取得
        await getCalendarDay();
    }

    // 年月更新
    function updateYearMonth(newData) {
        const year = newData.getFullYear();
        const month = (newData.getMonth() + 1).toString().padStart(2, '0');
        yearMonth.value = `${year}/${month}`;
    }

    // ----------------------------
    // 算術プロパティ
    // ----------------------------
    const formatSelectedDate = computed(() => formatDate(selectedDate.value));
    const isContent = computed(() => requestData.CngMethodType !== '1');
    // カウンセラー選択用のDateオブジェクト
    const calendarTime = computed(() => {
        // 時間部分を抽出
        return !utils.isNullEmpty(requestData.ReservationDt) ? requestData.ReservationDt.split(' ')[1] : '';
    });
    // カレンダー用のDateオブジェクト
    const calendarDate = computed(() => {
        return !utils.isNullEmpty(requestData.ReservationDt) ? new Date(requestData.ReservationDt) : selectedDate.value;
    });
    // ----------------------------
    // ライフサイクル
    // ----------------------------
    onMounted(async () => {
        // 遷移元の画面を判定する。
        if (from === 'ReserveDetailView') {
            // 予約方法取得API
            await getTabList();

            // タブ情報
            tabs.value = reserveStore.cngReserveTypes.map((type) => {
                return {
                    id: type === '1' ? 'changeModeTo1' : 'changeModeTo4',
                    name: type === '1' ? i18n.global.t('reservePlanningCounselor.parts.button1') : i18n.global.t('reservePlanningCounselor.parts.button2'),
                    class: 'width280',
                    type: type,
                };
            });

            // サービス詳細取得
            await getServiceDetail();

            // カレンダーから選択のカウンセラーの選択ブロックの上部に表示する日付情報
            const date = requestData.ReservationDt.replace(' ', 'T').replace(/\//g, '-') + ':00';
            selectedDate.value = new Date(date);
            updateYearMonth(selectedDate.value);
            if (requestData.CngReserveType === '1') {
                calendarComponent.value.initializeSelect();
            }

            // 予約方法のタブ表示に応じて、初期処理で実行するAPIを制御する。
            await consultationSelection();

            if (!commonStore.reservePageBack) {
                // 予約詳細画面からの遷移の場合。
                mergeResponce(requestData, reserveStore.reserveDetail);

                reserveStore.splitDateTime(reserveStore.reserveDetail.RequestDt1Start);
                selectedDate1.value = reserveStore.formatDate;
                selectedTime1Start.value = reserveStore.formatTime;
                reserveStore.splitDateTime(reserveStore.reserveDetail.RequestDt1End);
                selectedTime1End.value = reserveStore.formatTime;
                reserveStore.splitDateTime(reserveStore.reserveDetail.RequestDt2Start);
                selectedDate2.value = reserveStore.formatDate;
                selectedTime2Start.value = reserveStore.formatTime;
                reserveStore.splitDateTime(reserveStore.reserveDetail.RequestDt2End);
                selectedTime2End.value = reserveStore.formatTime;
                reserveStore.splitDateTime(reserveStore.reserveDetail.RequestDt3Start);
                selectedDate3.value = reserveStore.formatDate;
                selectedTime3Start.value = reserveStore.formatTime;
                reserveStore.splitDateTime(reserveStore.reserveDetail.RequestDt3End);
                selectedTime3End.value = reserveStore.formatTime;
            } else {
                reserveStore.splitDateTime(reserveStore.rsvdtlRequest.RequestDt1Start);
                selectedDate1.value = reserveStore.formatDate;
                selectedTime1Start.value = reserveStore.formatTime;
                reserveStore.splitDateTime(reserveStore.rsvdtlRequest.RequestDt1End);
                selectedTime1End.value = reserveStore.formatTime;
                reserveStore.splitDateTime(reserveStore.rsvdtlRequest.RequestDt2Start);
                selectedDate2.value = reserveStore.formatDate;
                selectedTime2Start.value = reserveStore.formatTime;
                reserveStore.splitDateTime(reserveStore.rsvdtlRequest.RequestDt2End);
                selectedTime2End.value = reserveStore.formatTime;
                reserveStore.splitDateTime(reserveStore.rsvdtlRequest.RequestDt3Start);
                selectedDate3.value = reserveStore.formatDate;
                selectedTime3Start.value = reserveStore.formatTime;
                reserveStore.splitDateTime(reserveStore.rsvdtlRequest.RequestDt3End);
                selectedTime3End.value = reserveStore.formatTime;
            }
        } else {
            // 予約詳細画面からの遷移では無く、各新規予約の画面からのフローで遷移してきた場合。
            // 前画面から戻ってきたか判定する。
            if (!commonStore.reservePageBack) {
                if (!reserveStore.restoreFlag) {
                    // 初期化
                    reserveStore.calendarTimeList = [];
                    reserveStore.counselorList = [];
                    reserveStore.roomList = [];
                    reserveStore.regionList = [];
                    reserveStore.areaList = [];
                    requestData.RegionPk = '';
                    requestData.AreaPk = '';
                    requestData.RequestDt1Start = '';
                    requestData.RequestDt1End = '';
                    requestData.RequestDt2Start = '';
                    requestData.RequestDt2End = '';
                    requestData.RequestDt3Start = '';
                    requestData.RequestDt3End = '';
                    requestData.RequestNote = '';
                    requestData.RoomPk = '';
                    requestData.CounselorPk = '';
                    requestData.ReservationDt = '';
                    // カレンダーに表示する日付の初期値は本日の日付
                    selectedDate.value = new Date();
                    updateYearMonth(selectedDate.value);
                    isSelect.value = false;
                    requestData.CngReserveType = '';
                } else {
                    // 前回選択の内容
                    if (requestData.CngReserveType === '1') {
                        // カレンダーの初期化
                        // if (setToday(currentDate.value) >= requestData.ReservationDt.split(' ')[0]) {
                        selectedDate.value = new Date();
                        updateYearMonth(selectedDate.value);
                        calendarComponent.value.initializeSelect();
                        // } else {
                        //     // カレンダーから選択のカウンセラーの選択ブロックの上部に表示する日付情報
                        //     selectedDate.value = new Date(requestData.ReservationDt);
                        //     updateYearMonth(selectedDate.value);
                        //     calendarComponent.value.initializeSelect();
                        // }
                    } else {
                        // 予約情報の初期化
                        requestData.RoomPk = '';
                        requestData.ReservationDt = '';
                        requestData.CounselorPk = '';
                        // reserveStore.splitDateTime(requestData.RequestDt1Start);
                        // selectedDate1.value = reserveStore.formatDate;
                        // selectedTime1Start.value = reserveStore.formatTime;
                        // reserveStore.splitDateTime(requestData.RequestDt1End);
                        // selectedTime1End.value = reserveStore.formatTime;
                        // reserveStore.splitDateTime(requestData.RequestDt2Start);
                        // selectedDate2.value = reserveStore.formatDate;
                        // selectedTime2Start.value = reserveStore.formatTime;
                        // reserveStore.splitDateTime(requestData.RequestDt2End);
                        // selectedTime2End.value = reserveStore.formatTime;
                        // reserveStore.splitDateTime(requestData.RequestDt3Start);
                        // selectedDate3.value = reserveStore.formatDate;
                        // selectedTime3Start.value = reserveStore.formatTime;
                        // reserveStore.splitDateTime(requestData.RequestDt3End);
                        // selectedTime3End.value = reserveStore.formatTime;
                    }
                }
            } else {
                if (requestData.CngReserveType === '1') {
                    // カレンダーから選択のカウンセラーの選択ブロックの上部に表示する日付情報
                    selectedDate.value = new Date(requestData.ReservationDt);
                    updateYearMonth(selectedDate.value);
                    calendarComponent.value.initializeSelect();
                } else {
                    reserveStore.splitDateTime(requestData.RequestDt1Start);
                    selectedDate1.value = reserveStore.formatDate;
                    selectedTime1Start.value = reserveStore.formatTime;
                    reserveStore.splitDateTime(requestData.RequestDt1End);
                    selectedTime1End.value = reserveStore.formatTime;
                    reserveStore.splitDateTime(requestData.RequestDt2Start);
                    selectedDate2.value = reserveStore.formatDate;
                    selectedTime2Start.value = reserveStore.formatTime;
                    reserveStore.splitDateTime(requestData.RequestDt2End);
                    selectedTime2End.value = reserveStore.formatTime;
                    reserveStore.splitDateTime(requestData.RequestDt3Start);
                    selectedDate3.value = reserveStore.formatDate;
                    selectedTime3Start.value = reserveStore.formatTime;
                    reserveStore.splitDateTime(requestData.RequestDt3End);
                    selectedTime3End.value = reserveStore.formatTime;
                }
            }

            // 前画面からの戻るフラグを初期化する。
            commonStore.reservePageBack = false;

            // 予約予約方法一覧APIを実行する。
            await getTabList();

            if (utils.isNullEmpty(requestData.CngReserveType)) {
                requestData.CngReserveType = reserveStore.cngReserveTypes[0];
            }

            // 取得した予約方法に応じて、カウンセラーと予約できるスケジュール方法を制御する。
            // 予約方法に応じて、画面のタブの表示を制御する。
            // 予約方法が1の場合はカレンダーでスケジューリング可能。
            // 予約方法が2の場合はプルダウンからカウンセリング場所などをスケジューリング可能。
            tabs.value = reserveStore.cngReserveTypes.map((type) => {
                return {
                    id: type === '1' ? 'changeModeTo1' : 'changeModeTo4',
                    name: type === '1' ? i18n.global.t('reservePlanningCounselor.parts.button1') : i18n.global.t('reservePlanningCounselor.parts.button2'),
                    class: '',
                    type: type,
                };
            });

            // 予約方法のタブ表示に応じて、初期処理で実行するAPIを制御する。
            await consultationSelection();
        }
        // 前画面からの戻るフラグを初期化する。
        commonStore.reservePageBack = false;
    });
</script>

<template>
    <section id="main">
        <section id="overlay" :style="{ display: counselorProfileModalDispFlg }">
            <!--カウンセラープロフィールモーダル-->
            <CounselorProfileComponent ref="counselorProfileComponent" @counselorProfileModal:close="counselorProfileComponentClose" />
        </section>
        <section id="overlay" :style="{ display: counselingRoomGuidanceModalDispFlg }">
            <!--施設モーダル-->
            <CounselingRoomGuidanceComponent ref="counselingRoomGuidanceComponent" @counselingRoomGuidanceModal:close="counselingRoomGuidanceClose" />
        </section>
        <MainHeader />
        <div id="contents">
            <div class="whiteBox">
                <h3 class="reserve">
                    <span>{{ $t('reservePlanningCounselor.parts.pageTitle1') }}</span>
                    <!-- ステップ表示 -->
                    <template v-if="languageStore.getLanguageValue === '1'">
                        <template v-if="from === 'consultation'">
                            <!--予約(相談内容から利用方法等選択)画面からのルート-->
                            <img src="/img/step/mode1_step4.png" class="step" width="410" height="46" />
                        </template>
                        <template v-if="from === 'program'">
                            <!--予約(プログラムからサービス選択)画面からのルート-->
                            <img src="/img/step/mode2_step4.png" class="step" width="410" height="46" />
                        </template>
                        <template v-else-if="from === 'personal'">
                            <!--予約(プログラムから利用方法など選択)画面からのルート-->
                            <img src="/img/step/mode3_step4.png" class="step" width="410" height="46" />
                        </template>
                    </template>
                    <template v-else>
                        <template v-if="from === 'consultation'">
                            <!--予約(相談内容から利用方法等選択)画面からのルート-->
                            <img src="/img/step/mode1_step4_en.png" class="step" width="410" height="46" />
                        </template>
                        <template v-if="from === 'program'">
                            <!--予約(プログラムからサービス選択)画面からのルート-->
                            <img src="/img/step/mode2_step4_en.png" class="step" width="410" height="46" />
                        </template>
                        <template v-else-if="from === 'personal'">
                            <!--予約(プログラムから利用方法など選択)画面からのルート-->
                            <img src="/img/step/mode3_step4_en.png" class="step" width="410" height="46" />
                        </template>
                    </template>
                </h3>
                <section class="whiteBoxContents scroll">
                    <!-- ステップ表示 -->
                    <template v-if="languageStore.getLanguageValue === '1'">
                        <template v-if="from === 'consultation'">
                            <!--予約(相談内容から利用方法等選択)画面からのルート-->
                            <img src="/img/step/mode1_step4.png" class="step" width="410" height="46" />
                        </template>
                        <template v-if="from === 'program'">
                            <!--予約(プログラムからサービス選択)画面からのルート-->
                            <img src="/img/step/mode2_step4.png" class="step" width="410" height="46" />
                        </template>
                        <template v-else-if="from === 'personal'">
                            <!--予約(プログラムから利用方法など選択)画面からのルート-->
                            <img src="/img/step/mode3_step4.png" class="step" width="410" height="46" />
                        </template>
                    </template>
                    <template v-else>
                        <template v-if="from === 'consultation'">
                            <!--予約(相談内容から利用方法等選択)画面からのルート-->
                            <img src="/img/step/mode1_step4_en.png" class="step" width="410" height="46" />
                        </template>
                        <template v-if="from === 'program'">
                            <!--予約(プログラムからサービス選択)画面からのルート-->
                            <img src="/img/step/mode2_step4_en.png" class="step" width="410" height="46" />
                        </template>
                        <template v-else-if="from === 'personal'">
                            <!--予約(プログラムから利用方法など選択)画面からのルート-->
                            <img src="/img/step/mode3_step4_en.png" class="step" width="410" height="46" />
                        </template>
                    </template>
                    <Tab :tabs="tabs" :modelValue="requestData.CngReserveType" v-model="requestData.CngReserveType" @changeTab="tabChange" />
                    <template v-if="requestData.CngReserveType === '1'">
                        <!--「カレンダーから選択」タブが選択されている場合-->
                        <template v-if="isContent">
                            <!--利用方法が「オンライン」または「電話」で選択されている場合-->
                            <h4 id="reserveMethod">{{ message }}</h4>
                        </template>
                        <template v-else>
                            <section class="form" style="padding-left: unset; padding-right: unset; scrollbar-gutter: unset; margin-top: 12px; margin-bottom: 20px; overflow: unset">
                                <!--利用方法が「対面」で選択されている場合-->
                                <dl>
                                    <!--予約場所-->
                                    <dt>
                                        <ul class="formLabel">
                                            <li>
                                                <span>{{ $t('reservePlanningCounselor.parts.dl1') }}</span>
                                            </li>
                                            <li>
                                                <Tooltip class-bottom>{{ $t('reservePlanningCounselor.parts.blowoutInformation1') }}</Tooltip>
                                            </li>
                                        </ul>
                                    </dt>
                                    <dd>
                                        <ul class="formInput">
                                            <li style="margin-right: 20px">
                                                <BaseSelect :options="reserveStore.roomList" v-model="requestData.RoomPk" required @change="changeRoom" />
                                            </li>
                                            <li>
                                                <button type="button" class="innerBtn" @click="counselingRoomGuidanceOpen(requestData.RoomPk)">{{ $t('reservePlanningCounselor.parts.linkButton1') }}</button>
                                            </li>
                                        </ul>
                                    </dd>
                                </dl>
                            </section>
                        </template>
                        <section id="calendar" :class="[isContent ? '' : 'place']">
                            <!--カレンダーから選択-->
                            <dl>
                                <!--予約日時・カウンセラー選択（カレンダー）-->
                                <dt>
                                    <!--予定日時・カウンセラー-->
                                    <ul class="formLabel">
                                        <li class="widthAuto">
                                            <span>
                                                {{ $t('reservePlanningCounselor.parts.dl2') }}
                                                {{ $t('reservePlanningCounselor.parts.dl3') }}
                                            </span>
                                        </li>
                                        <li class="widthAuto">
                                            <Tooltip :classWidth="344">{{ $t('reservePlanningCounselor.parts.blowoutInformation2') }}</Tooltip>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <!--カレンダーパーツ-->
                                    <Calendar ref="calendarComponent" :reserveDateList="reserveStore.calendarTimeList" :reserveDate="calendarDate" :resetFlag="dateReset" @dateSelected="calendarDateSelected" @yearMonth="calendarYearMonth" />
                                </dd>
                                <dd v-if="isSelect">
                                    <!--カウンセラー選択パーツ-->
                                    <CounselorBox :counselores="reserveStore.counselorList" :counselorPk="requestData.CounselorPk" :time="calendarTime" @childEmit="radioChange" @counselorProfileTarget="counselorProfileComponentOpen" />
                                </dd>
                            </dl>
                        </section>
                    </template>
                    <template v-else-if="requestData.CngReserveType === '2'">
                        <!--「ご希望の場所・時間帯をご連絡」タブが選択されている場合-->
                        <template v-if="isContent">
                            <!--利用方法が「オンライン」または「電話」で選択されている場合-->
                            <h4 id="reserveMethod">{{ message }}</h4>
                        </template>
                        <template v-else>
                            <section class="form" style="margin-top: 12px; margin-bottom: 20px">
                                <!--利用方法が「対面」で選択されている場合-->
                                <dl>
                                    <!--希望地域-->
                                    <dt>
                                        <ul class="formLabel">
                                            <li>
                                                <label>{{ $t('reservePlanningCounselor.parts.dl4') }}</label>
                                            </li>
                                            <li class="must">
                                                <span>{{ $t('common.parts.must') }}</span>
                                            </li>
                                        </ul>
                                    </dt>
                                    <dd>
                                        <ul class="formInput">
                                            <li>
                                                <BaseSelect :options="reserveStore.regionList" v-model="requestData.RegionPk" required @childEmit="regionChange" />
                                            </li>
                                        </ul>
                                    </dd>
                                </dl>
                                <dl>
                                    <!--希望地区-->
                                    <dt>
                                        <ul class="formLabel">
                                            <li>
                                                <label>{{ $t('reservePlanningCounselor.parts.dl5') }}</label>
                                            </li>
                                            <li class="must">
                                                <span>{{ $t('common.parts.must') }}</span>
                                            </li>
                                        </ul>
                                    </dt>
                                    <dd>
                                        <ul class="formInput">
                                            <li>
                                                <BaseSelect :options="reserveStore.areaList" v-model="requestData.AreaPk" required />
                                            </li>
                                        </ul>
                                    </dd>
                                </dl>
                            </section>
                        </template>
                        <section class="form">
                            <!--ご希望の場所・時間帯をご連絡-->
                            <dl>
                                <!--ご希望日時1-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('reservePlanningCounselor.parts.dl6') }}</label>
                                        </li>
                                        <li class="must">
                                            <span>{{ $t('common.parts.must') }}</span>
                                        </li>
                                        <li>
                                            <Tooltip class-bottom>{{ $t('reservePlanningCounselor.parts.blowoutInformation3') }}</Tooltip>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput reserveDate">
                                        <li>
                                            <label class="calendar">
                                                <input type="date" :class="[selectedDate1 !== '' ? 'fillterText' : 'fillterText blank']" :placeholder="$t('common.parts.selectDateDefaultShort')" v-model="selectedDate1" />
                                            </label>
                                        </li>
                                        <li>
                                            <TimeSelect v-model="selectedTime1Start" :minute="0" :childEmit="dateSet('1')" />
                                        </li>
                                        <li>
                                            <span>～</span>
                                        </li>
                                        <li>
                                            <TimeSelect v-model="selectedTime1End" :minute="30" :childEmit="dateSet('1')" />
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl>
                                <!--ご希望日時2-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('reservePlanningCounselor.parts.dl7') }}</label>
                                        </li>
                                        <li class="must">
                                            <span>{{ $t('common.parts.must') }}</span>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput reserveDate">
                                        <li>
                                            <label class="calendar">
                                                <input type="date" :class="[selectedDate2 !== '' ? 'fillterText' : 'fillterText blank']" :placeholder="$t('common.parts.selectDateDefaultShort')" v-model="selectedDate2" />
                                            </label>
                                        </li>
                                        <li>
                                            <TimeSelect v-model="selectedTime2Start" :minute="0" :childEmit="dateSet('2')" />
                                        </li>
                                        <li>
                                            <span>～</span>
                                        </li>
                                        <li>
                                            <TimeSelect v-model="selectedTime2End" :minute="30" :childEmit="dateSet('2')" />
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl>
                                <!--ご希望日時3-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('reservePlanningCounselor.parts.dl8') }}</label>
                                        </li>
                                        <li class="must">
                                            <span>{{ $t('common.parts.must') }}</span>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput reserveDate">
                                        <li>
                                            <label class="calendar">
                                                <input type="date" :class="[selectedDate3 !== '' ? 'fillterText' : 'fillterText blank']" :placeholder="$t('common.parts.selectDateDefaultShort')" v-model="selectedDate3" />
                                            </label>
                                        </li>
                                        <li>
                                            <TimeSelect v-model="selectedTime3Start" :minute="0" :childEmit="dateSet('3')" />
                                        </li>
                                        <li>
                                            <span>～</span>
                                        </li>
                                        <li>
                                            <TimeSelect v-model="selectedTime3End" :minute="30" :childEmit="dateSet('3')" />
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                            <dl>
                                <!--連絡事項-->
                                <dt>
                                    <ul class="formLabel">
                                        <li>
                                            <label>{{ $t('reservePlanningCounselor.parts.dl9') }}</label>
                                        </li>
                                    </ul>
                                </dt>
                                <dd>
                                    <ul class="formInput">
                                        <li>
                                            <textarea class="textAreaTypeB" :placeholder="$t('reservePlanningCounselor.parts.placeholder1')" v-model="requestData.RequestNote" />
                                            <p class="description">{{ $t('reservePlanningCounselor.parts.pageInformation4') }}</p>
                                        </li>
                                    </ul>
                                </dd>
                            </dl>
                        </section>
                    </template>
                </section>
                <section class="whiteBoxFooter">
                    <ul class="btnArea center">
                        <li>
                            <!--戻る-->
                            <button type="button" class="btnNormal" @click="onBack">{{ $t('common.parts.backPage') }}</button>
                        </li>
                        <li>
                            <!--進む-->
                            <button type="button" class="btnPrimary" @click="onAdvance">{{ $t('common.parts.nextPage') }}</button>
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    </section>
</template>

<style></style>
